import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface DatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
}

export default function DatePicker({
  value,
  onChange,
  placeholder = "Selecione a data"
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (value) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return null;
  });
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideCalendar = calendarRef.current?.contains(target);
      
      if (!isInsideContainer && !isInsideCalendar) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      // Criar data sem problemas de fuso horário
      const [year, month, day] = value.split('-').map(Number);
      setSelectedDate(new Date(year, month - 1, day));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const calculatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const calendarWidth = 280; // Largura mínima do calendário
      const calendarHeight = 320; // Altura aproximada do calendário
      
      let left = rect.left;
      let top = rect.bottom + 4;
      
      // Posicionar mais à esquerda para evitar ficar colado no fim da tela
      // Se estiver na sidebar (lado direito), posicionar mais à esquerda
      if (left > window.innerWidth / 2) {
        left = Math.max(left - 80, 20);
      } else {
        left = Math.max(left - 30, 20);
      }
      
      // Verificar se o calendário vai sair pela direita
      if (left + calendarWidth > window.innerWidth) {
        left = window.innerWidth - calendarWidth - 20;
      }
      
      // Verificar se o calendário vai sair pela esquerda
      if (left < 20) {
        left = 20;
      }
      
      // Verificar se o calendário vai sair pela parte inferior
      if (top + calendarHeight > window.innerHeight) {
        // Posicionar acima do input
        top = rect.top - calendarHeight - 4;
      }
      
      // Verificar se ainda está saindo pela parte superior
      if (top < 20) {
        top = 20;
      }
      
      setCalendarPosition({ top, left });
    }
  };

  const formatDateForDisplay = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateForValue = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Dias do mês anterior
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonth.getDate() - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonth.getDate() - i)
      });
    }
    
    // Dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }
    
    // Dias do próximo mês para completar a grade
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }
    
    return days;
  };



  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange(formatDateForValue(date));
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div ref={containerRef} style={{ position: 'relative', zIndex: isOpen ? 1000 : 1 }}>
      {/* Input Field */}
      <input
        type="text"
        placeholder={placeholder}
        value={selectedDate ? formatDateForDisplay(selectedDate) : ''}
        readOnly
        onClick={() => {
          if (!isOpen) {
            calculatePosition();
          }
          setIsOpen(!isOpen);
        }}
        style={{
          width: '100%',
          height: '48px',
          background: 'var(--cffffff)',
          border: '1px solid #e2e8f0',
          outline: 'none',
          borderRadius: '8px',
          color: 'var(--c2d3748)',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '24px',
          transition: 'all 0.2s ease',
          fontFamily: 'inherit',
          cursor: 'pointer'
        }}
        onFocus={(e) => {
          e.target.style.border = '1px solid #a0aec0';
        }}
        onBlur={(e) => {
          e.target.style.border = '1px solid #e2e8f0';
        }}
      />

      {/* Calendar Dropdown */}
      {isOpen && createPortal(
        <div ref={calendarRef} style={{
          position: 'fixed',
          top: calendarPosition.top,
          left: calendarPosition.left,
          backgroundColor: 'var(--surface-card)',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1001,
          padding: '16px',
          minWidth: '280px'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <button
              onClick={handlePrevMonth}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                background: 'var(--surface-card)',
                color: 'var(--c2d3748)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              &lt;
            </button>
            
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--c2d3748)'
            }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            
            <button
              onClick={handleNextMonth}
              style={{
                width: '32px',
                height: '32px',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                background: 'var(--surface-card)',
                color: 'var(--c2d3748)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              &gt;
            </button>
          </div>

          {/* Days of Week */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '8px'
          }}>
            {dayNames.map((day) => (
              <div
                key={day}
                style={{
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--c2d3748)',
                  padding: '8px 4px'
                }}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px'
          }}>
            {days.map((day, index) => (
              <button
                key={index}
                onClick={() => handleDateSelect(day.date)}
                style={{
                  width: '32px',
                  height: '32px',
                  border: isSelected(day.date) ? '1px solid #E52940' : 'none',
                  borderRadius: '4px',
                  background: isToday(day.date) ? 'var(--cf7fafc)' : 'var(--surface-card)',
                  color: day.isCurrentMonth ? 'var(--c2d3748)' : 'var(--ca0aec0)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: isSelected(day.date) ? 'bold' : (isToday(day.date) ? '600' : '400'),
                  transition: 'all 0.2s ease',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => {
                  if (day.isCurrentMonth) {
                    e.currentTarget.style.backgroundColor = 'var(--cf7fafc)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (day.isCurrentMonth) {
                    e.currentTarget.style.backgroundColor = 'var(--surface-card)';
                  }
                }}
              >
                {day.day}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
